import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import UserAvatar from '../../components/common/UserAvatar';
import { MessageSquare, Send, Search, ArrowLeft, UserCheck, UserPlus, Zap, X, Plus, Pencil, Trash2, AlertTriangle, TrendingUp, Store, User, Sparkles } from 'lucide-react';

const RoleBadge = ({ role }) => {
  const isProvider = role === 'provider';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isProvider ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}
      data-testid={`role-badge-${role}`}
    >
      {isProvider ? <Store className="w-3 h-3" /> : <User className="w-3 h-3" />}
      {isProvider ? 'Provider' : 'Customer'}
    </span>
  );
};

const API = `${window.location.origin}/api`;

export default function AdminMessages() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin';
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // provider_id
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [cannedReplies, setCannedReplies] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [activeMeta, setActiveMeta] = useState(null); // selected user before a thread exists
  const [showPeople, setShowPeople] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support-chat/conversations`, { headers });
      setConversations(res.data.conversations || []);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchMessages = useCallback(async (providerId) => {
    try {
      const res = await axios.get(`${API}/support-chat/conversations/${providerId}/messages`, { headers });
      setMessages(res.data.messages || []);
      // tell sidebar/mobile badges to refresh after marking read
      window.dispatchEvent(new CustomEvent('support-unread-refresh'));
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCannedReplies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support-chat/canned-replies`, { headers });
      setCannedReplies(res.data.canned_replies || []);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchConversations(); fetchCannedReplies(); }, [fetchConversations, fetchCannedReplies]);

  // poll conversation list
  useEffect(() => {
    const t = setInterval(fetchConversations, 8000);
    return () => clearInterval(t);
  }, [fetchConversations]);

  // poll open thread
  useEffect(() => {
    if (!active) return;
    fetchMessages(active);
    const t = setInterval(() => {
      if (activeRef.current) fetchMessages(activeRef.current);
    }, 3000);
    return () => clearInterval(t);
  }, [active, fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openConversation = (c) => {
    setActiveMeta(null);
    setActive(c.provider_id);
    setConversations((prev) => prev.map((x) => x.provider_id === c.provider_id ? { ...x, unread_for_staff: 0 } : x));
  };

  const startWithUser = (u) => {
    setShowPeople(false);
    const existing = conversations.find((c) => c.provider_id === u.id);
    if (existing) { openConversation(existing); return; }
    setMessages([]);
    setActiveMeta({ provider_id: u.id, provider_name: u.name, provider_email: u.email, provider_image: u.profile_image, role: u.role });
    setActive(u.id);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const send = async () => {
    const content = input.trim();
    if (!content || !active) return;
    setSending(true);
    setInput('');
    const optimistic = { id: `tmp-${Date.now()}`, provider_id: active, sender_type: 'staff', sender_name: user?.name || 'Support', content, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      await axios.post(`${API}/support-chat/conversations/${active}/messages`, { content }, { headers });
      fetchMessages(active);
      fetchConversations();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const toggleClaim = async (claimed) => {
    if (!active) return;
    const endpoint = claimed ? 'release' : 'claim';
    try {
      const res = await axios.post(`${API}/support-chat/conversations/${active}/${endpoint}`, {}, { headers });
      const updated = res.data.conversation;
      setConversations((prev) => prev.map((x) => x.provider_id === active ? { ...x, ...updated } : x));
    } catch (e) { /* ignore */ }
  };

  const toggleAiMode = async (enabled) => {
    if (!active) return;
    try {
      const res = await axios.post(`${API}/support-chat/conversations/${active}/ai-mode`, { enabled }, { headers });
      const updated = res.data.conversation;
      setConversations((prev) => prev.map((x) => x.provider_id === active ? { ...x, ...updated } : x));
    } catch (e) { /* ignore */ }
  };

  const insertCanned = (reply) => {
    const content = reply?.content || '';
    setInput((prev) => (prev && prev.trim() ? `${prev}\n${content}` : content));
    setShowCanned(false);
    setTimeout(() => inputRef.current?.focus(), 50);
    // track usage for reply insights (fire-and-forget)
    if (reply?.id) {
      axios.post(`${API}/support-chat/canned-replies/${reply.id}/use`, {}, { headers })
        .then(() => fetchCannedReplies())
        .catch(() => {});
    }
  };

  const filtered = conversations.filter((c) =>
    !search || (c.provider_name || '').toLowerCase().includes(search.toLowerCase()) || (c.provider_email || '').toLowerCase().includes(search.toLowerCase())
  );
  const activeConvo = conversations.find((c) => c.provider_id === active);
  const headerInfo = activeConvo || activeMeta;
  const assignedTo = activeConvo?.assigned_to;
  const assignedName = activeConvo?.assigned_name;
  const claimedByMe = assignedTo && assignedTo === user?.id;
  const claimedByOther = assignedTo && assignedTo !== user?.id;
  const aiMode = !!activeConvo?.ai_mode;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 md:ml-64 pt-16 md:pt-4 p-0 md:p-6 overflow-x-hidden">
        <div className="hidden md:flex items-center justify-between mb-4 px-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Messages</h1>
            <p className="text-sm text-slate-500">Chat with your customers and service providers</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPeople(true)}
              className="flex items-center gap-2 px-3 h-10 rounded-xl bg-amber-500 text-slate-900 text-sm font-semibold hover:bg-amber-600 transition-colors"
              data-testid="new-conversation-btn"
            >
              <Plus className="w-4 h-4" /> New conversation
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowManage(true)}
                className="flex items-center gap-2 px-3 h-10 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                data-testid="manage-canned-replies-btn"
              >
                <Zap className="w-4 h-4" /> Quick Replies
              </button>
            )}
          </div>
        </div>

        <div className="bg-white md:rounded-2xl md:border md:border-stone-200 overflow-hidden flex h-[calc(100vh-7rem)] md:h-[calc(100vh-10rem)]">
          {/* Conversation list */}
          <div className={`${active ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-stone-200`}>
            <div className="p-3 border-b border-stone-100 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-9 pr-3 h-10 bg-stone-100 rounded-xl text-sm focus:outline-none"
                  data-testid="chat-search-input"
                />
              </div>
              <button
                onClick={() => setShowPeople(true)}
                className="w-10 h-10 rounded-xl bg-amber-500 text-slate-900 flex items-center justify-center flex-shrink-0 hover:bg-amber-600 transition-colors md:hidden"
                data-testid="new-conversation-btn-mobile"
                title="New conversation"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : filtered.map((c) => (
                <button
                  key={c.provider_id}
                  onClick={() => openConversation(c)}
                  data-testid={`conversation-${c.provider_id}`}
                  className={`w-full flex items-center gap-3 p-3 text-left hover:bg-stone-50 border-b border-stone-100 ${active === c.provider_id ? 'bg-amber-50' : ''}`}
                >
                  <UserAvatar src={c.provider_image} name={c.provider_name} role={c.role || 'provider'} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-900 text-sm truncate">{c.provider_name}</p>
                      {c.unread_for_staff > 0 && (
                        <span className="ml-2 bg-amber-500 text-white text-xs font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center" data-testid={`unread-${c.provider_id}`}>
                          {c.unread_for_staff}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.last_message}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <RoleBadge role={c.role || 'provider'} />
                      {c.assigned_to && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${c.assigned_to === user?.id ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}
                          data-testid={`assigned-chip-${c.provider_id}`}
                        >
                          <UserCheck className="w-3 h-3" />
                          {c.assigned_to === user?.id ? 'You' : c.assigned_name}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Chat panel */}
          <div className={`${active ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0 min-h-0`}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 flex-col gap-3">
                <MessageSquare className="w-14 h-14 opacity-30" />
                <p>Select a conversation or start a new one</p>
                <button
                  onClick={() => setShowPeople(true)}
                  className="flex items-center gap-2 px-4 h-10 rounded-xl bg-amber-500 text-slate-900 text-sm font-semibold hover:bg-amber-600 transition-colors"
                  data-testid="new-conversation-empty-btn"
                >
                  <Plus className="w-4 h-4" /> New conversation
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3 border-b border-stone-200 bg-white flex-shrink-0">
                  <button className="md:hidden p-1" onClick={() => { setActive(null); setActiveMeta(null); }} data-testid="chat-back-btn">
                    <ArrowLeft className="w-5 h-5 text-slate-600" />
                  </button>
                  <UserAvatar src={headerInfo?.provider_image} name={headerInfo?.provider_name} role={headerInfo?.role || 'provider'} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 truncate">{headerInfo?.provider_name || 'User'}</p>
                      <RoleBadge role={headerInfo?.role || 'provider'} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">{headerInfo?.provider_email}</p>
                  </div>
                  {activeConvo && (
                    <button
                      onClick={() => toggleAiMode(!aiMode)}
                      className={`flex items-center gap-1.5 px-3 h-9 rounded-xl text-xs font-semibold transition-colors flex-shrink-0 ${
                        aiMode
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          : 'bg-stone-100 text-slate-600 hover:bg-stone-200'
                      }`}
                      data-testid="ai-mode-toggle-btn"
                      title={aiMode ? 'Juan AI is auto-replying. Click to switch to human.' : 'Let Juan AI auto-reply to this user.'}
                    >
                      <Sparkles className="w-4 h-4" /> {aiMode ? 'AI: On' : 'AI: Off'}
                    </button>
                  )}
                  {activeConvo && (
                    <button
                      onClick={() => toggleClaim(!!claimedByMe)}
                      className={`flex items-center gap-1.5 px-3 h-9 rounded-xl text-xs font-semibold transition-colors flex-shrink-0 ${
                        claimedByMe
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-slate-900 text-white hover:bg-slate-800'
                      }`}
                      data-testid="claim-conversation-btn"
                    >
                      {claimedByMe ? <><UserCheck className="w-4 h-4" /> Release</> : <><UserPlus className="w-4 h-4" /> Claim</>}
                    </button>
                  )}
                </div>

                {aiMode && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs" data-testid="ai-mode-banner">
                    <Sparkles className="w-4 h-4 flex-shrink-0" />
                    <span>Juan (AI) is automatically answering this user. Sending a reply will take over from Juan.</span>
                  </div>
                )}

                {claimedByOther && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs" data-testid="claimed-warning">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>This conversation is being handled by <strong>{assignedName}</strong>. Coordinate before replying to avoid double responses.</span>
                  </div>
                )}

                <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-stone-50">
                  {messages.map((m) => {
                    const mine = m.sender_type === 'staff';
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? (m.is_ai ? 'bg-amber-500 text-slate-900 rounded-br-sm' : 'bg-slate-900 text-white rounded-br-sm') : 'bg-white border border-stone-200 text-slate-800 rounded-bl-sm'}`}>
                          {!mine && <p className="text-xs font-medium text-amber-600 mb-0.5">{m.sender_name}</p>}
                          {mine && m.is_ai && <p className="text-[10px] font-bold mb-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Juan (AI)</p>}
                          <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                          <p className={`text-[10px] mt-1 ${mine ? 'text-slate-300' : 'text-slate-400'}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="p-3 border-t border-stone-200 bg-white flex items-center gap-2 relative flex-shrink-0">
                  {/* Canned replies */}
                  <div className="relative">
                    <button
                      onClick={() => setShowCanned((s) => !s)}
                      className="w-11 h-11 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors flex-shrink-0"
                      data-testid="canned-replies-toggle"
                      title="Quick replies"
                    >
                      <Zap className="w-5 h-5 text-slate-600" />
                    </button>
                    {showCanned && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setShowCanned(false)} />
                        <div className="absolute bottom-14 left-0 w-72 max-h-72 overflow-y-auto bg-white rounded-2xl shadow-2xl border border-stone-200 z-40 p-2" data-testid="canned-replies-dropdown">
                          <div className="flex items-center justify-between px-2 py-1.5">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick Replies</p>
                            {isAdmin && (
                              <button onClick={() => { setShowCanned(false); setShowManage(true); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium" data-testid="open-manage-from-dropdown">Manage</button>
                            )}
                          </div>
                          {cannedReplies.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6 px-2">No quick replies yet{isAdmin ? '. Click Manage to add one.' : '.'}</p>
                          ) : cannedReplies.map((r) => (
                            <button
                              key={r.id}
                              onClick={() => insertCanned(r)}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-stone-50 transition-colors"
                              data-testid={`canned-reply-${r.id}`}
                            >
                              <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                              <p className="text-xs text-slate-500 line-clamp-2">{r.content}</p>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type a message..."
                    className="flex-1 h-11 px-4 bg-stone-100 rounded-full text-sm focus:outline-none"
                    data-testid="chat-message-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !input.trim()}
                    className="w-11 h-11 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center transition-colors flex-shrink-0"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-5 h-5 text-slate-900" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showManage && isAdmin && (
        <CannedRepliesManager
          replies={cannedReplies}
          headers={headers}
          onClose={() => setShowManage(false)}
          onChanged={fetchCannedReplies}
        />
      )}

      {showPeople && (
        <PeoplePicker
          headers={headers}
          onClose={() => setShowPeople(false)}
          onSelect={startWithUser}
        />
      )}
    </div>
  );
}

function PeoplePicker({ headers, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (q, r) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/support-chat/users`, { headers, params: { q, role: r } });
      setResults(res.data.users || []);
    } catch (e) { setResults([]); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query, role), 250);
    return () => clearTimeout(t);
  }, [query, role, search]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-20 bg-black/50" data-testid="people-picker-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[75vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Plus className="w-5 h-5 text-amber-500" /> New conversation</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-people-picker"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-3 h-11 bg-stone-100 rounded-xl text-sm focus:outline-none"
              data-testid="people-search-input"
            />
          </div>
          <div className="flex gap-2">
            {[['', 'All'], ['customer', 'Customers'], ['provider', 'Providers']].map(([val, label]) => (
              <button
                key={val || 'all'}
                onClick={() => setRole(val)}
                className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${role === val ? 'bg-slate-900 text-white' : 'bg-stone-100 text-slate-600 hover:bg-stone-200'}`}
                data-testid={`people-filter-${val || 'all'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {loading ? (
            <p className="text-center text-slate-400 text-sm py-8">Searching...</p>
          ) : results.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-8">No users found</p>
          ) : results.map((u) => (
            <button
              key={u.id}
              onClick={() => onSelect(u)}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-stone-50 transition-colors text-left"
              data-testid={`people-result-${u.id}`}
            >
              <UserAvatar src={u.profile_image} name={u.name} role={u.role} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
              </div>
              <RoleBadge role={u.role} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CannedRepliesManager({ replies, headers, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // reply object or null
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startNew = () => { setEditing({}); setTitle(''); setContent(''); setError(''); };
  const startEdit = (r) => { setEditing(r); setTitle(r.title); setContent(r.content); setError(''); };

  const save = async () => {
    if (!title.trim() || !content.trim()) { setError('Title and content are required'); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/support-chat/canned-replies/${editing.id}`, { title, content }, { headers });
      } else {
        await axios.post(`${API}/support-chat/canned-replies`, { title, content }, { headers });
      }
      setEditing(null);
      await onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/support-chat/canned-replies/${id}`, { headers });
      await onChanged();
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="canned-manager-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Quick Replies</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-canned-manager"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Payout timeline"
                  className="mt-1 w-full h-11 px-3 bg-stone-100 rounded-xl text-sm focus:outline-none"
                  data-testid="canned-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Message</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="The reply text staff can insert in one tap..."
                  rows={5}
                  className="mt-1 w-full p-3 bg-stone-100 rounded-xl text-sm focus:outline-none resize-none"
                  data-testid="canned-content-input"
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setEditing(null)} className="px-4 h-10 rounded-xl text-slate-600 hover:bg-stone-100 text-sm font-medium">Cancel</button>
                <button onClick={save} disabled={saving} className="px-4 h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="save-canned-btn">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button onClick={startNew} className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-dashed border-stone-300 text-slate-600 hover:border-amber-400 hover:text-amber-600 transition-colors mb-4 text-sm font-medium" data-testid="add-canned-btn">
                <Plus className="w-4 h-4" /> New quick reply
              </button>
              {replies.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-8">No quick replies yet</p>
              ) : (
                <div className="space-y-2">
                  {[...replies].sort((a, b) => (b.use_count || 0) - (a.use_count || 0)).map((r) => (
                    <div key={r.id} className="flex items-start gap-3 p-3 rounded-xl border border-stone-200" data-testid={`manage-canned-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-800">{r.title}</p>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`canned-usecount-${r.id}`}>
                            <TrendingUp className="w-3 h-3" /> {r.use_count || 0} use{(r.use_count || 0) === 1 ? '' : 's'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{r.content}</p>
                      </div>
                      <button onClick={() => startEdit(r)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`edit-canned-${r.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(r.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-canned-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
